import React, { useEffect } from "react";
import { X } from "lucide-react";

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string} props.title
 * @param {React.ReactNode} props.children
 * @param {() => void} props.onClose
 * @param {React.ReactNode} [props.footer]
 * @param {string} [props.className]
 */
export default function BaseModal({
  open,
  title,
  children,
  onClose,
  footer,
  className = "",
}) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200]">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[1px]"
        role="presentation"
        onClick={onClose}
      />
      <div className="absolute inset-x-0 top-[80px] bottom-[56px] lg:bottom-0 flex items-center justify-center overflow-y-auto p-2 sm:p-6">
        <section
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={(e) => e.stopPropagation()}
          className={[
            "w-full max-w-[660px] max-h-full rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden flex flex-col",
            className,
          ].join(" ")}
        >
          <header className="px-2 md:px-5 py-2 md:py-4 border-b border-gray-100 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-lg font-extrabold text-gray-900 truncate">
                {title}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-10 h-10 rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 flex items-center justify-center shrink-0"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto">{children}</div>

          {footer ? (
            <footer className="px-2 md:px-5 py-2 md:py-4 border-t border-gray-100 bg-white">
              {footer}
            </footer>
          ) : null}
        </section>
      </div>
    </div>
  );
}

